import React from 'react';
import moment from 'moment/moment';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Grid,
  Group,
  Modal as UiModal,
  Radio,
  RadioGroup,
  RHFSelect,
  RHFTextInput,
  Stack,
  Text,
} from '@cowbell/ui';

import { saveBlobFile } from '../../utils/appUtils';
import {
  downloadAWSDoc,
  downloadPolicyCertificate,
  fetchAdditonalInsured,
  sendPolicyCertificateEmail,
} from '../../policies/PolicyService';
import { useAPIErrorHandler } from '../hooks/useAPIErrorHandler';
import useNotifications from '../../_lib/notifications/useNotifications';
import usePlatformRegion from '../../utils/hooks/usePlatformRegion';
import { UsStatesFull } from '../../utils/USState';
import { AuStatesFull } from '../../utils/AUState';
import { globalSchema } from '../globalValidations';
import { DIALOG_DROPDOWN_PROPS } from '../dialogDropdown';

/** The policy fields the certificate reads. A policy table row carries them. */
export interface CertificatePolicy {
  readonly id: string;
  readonly accountId: string;
  readonly policyNumber: string;
  readonly companyName: string;
  readonly created: string | number;
  readonly product: string;
}

export interface AdditionalInsured {
  readonly id: string | number;
  readonly businessName: string;
  readonly address1: string;
  readonly city: string;
  readonly state: string;
  readonly zipCode: string;
}

interface CertificateHolderValues {
  readonly organizationName: string;
  readonly address1: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
}

const MODAL_Z_INDEX = 1400;
const HOLDER_FORM_ID = 'certificateHolderForm';

export const DOWNLOAD_CHOICE = 'download';
export const HOLDER_CHOICE = 'holder';
const INSURED_PREFIX = 'insured:';

export const insuredChoice = (insured: AdditionalInsured) =>
  `${INSURED_PREFIX}${insured.id}`;

export const additionalInsuredLabel = (insured: AdditionalInsured) =>
  `Additional insured ${insured.businessName} (${insured.address1}, ${insured.city}, ${insured.state}, ${insured.zipCode})`;

/** The additional insured a choice names, or null for the other two choices. */
export const insuredIdOf = (choice: string): string | null =>
  choice.startsWith(INSURED_PREFIX)
    ? choice.slice(INSURED_PREFIX.length)
    : null;

/** What a request to email the certificate sends, for an additional insured or a certificate holder. */
export function certificateEmailPayload(
  policy: CertificatePolicy,
  choice: string,
  holder?: CertificateHolderValues
) {
  const insuredId = insuredIdOf(choice);
  if (insuredId !== null) {
    return {
      docType: 'Additional_Insured',
      additionalInsuredId: insuredId,
      policyId: policy.id,
    };
  }
  return { ...holder, policyId: policy.id, docType: 'Certificate_Holder' };
}

const holderSchema = (isAU: boolean) =>
  Yup.object().shape({
    organizationName: Yup.string().required('Please enter an organization.'),
    address1: Yup.string().required('Please enter an address.'),
    city: Yup.string().required('Please enter a city.'),
    state: Yup.string().required(
      `Please select a ${isAU ? 'state or territory' : 'state'}.`
    ),
    zip: isAU ? globalSchema.auPostalCode : globalSchema.zipCode,
  });

function CertificateHolderFields() {
  const { isAU } = usePlatformRegion();
  return (
    <Grid gutter="md">
      <Grid.Col span={12}>
        <RHFTextInput
          name="organizationName"
          label="Organization name"
          required
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 5 }}>
        <RHFTextInput name="address1" label="Address" required />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 3 }}>
        <RHFTextInput name="city" label="City" required />
      </Grid.Col>
      <Grid.Col span={{ base: 6, sm: 2 }}>
        <RHFSelect
          name="state"
          label={isAU ? 'State/Territory' : 'State'}
          data={isAU ? AuStatesFull : UsStatesFull}
          searchable
          required
          comboboxProps={DIALOG_DROPDOWN_PROPS}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 6, sm: 2 }}>
        <RHFTextInput
          name="zip"
          label={isAU ? 'Postal code' : 'Zip code'}
          required
        />
      </Grid.Col>
    </Grid>
  );
}

interface DownloadEmailCertificateProps {
  readonly data: CertificatePolicy;
  readonly close: () => void;
  /** The modal host's open state. */
  readonly open?: boolean;
}

/**
 * Generate Policy Certificate: download the certificate of insurance, or have it emailed for one of the policy's
 * additional insureds or for a certificate holder the broker enters.
 */
export const DownloadEmailCertificate = ({
  data: policy,
  close,
  open = true,
}: DownloadEmailCertificateProps) => {
  const notifications = useNotifications();
  const handleAPIError = useAPIErrorHandler();
  const { isAU } = usePlatformRegion();
  const [choice, setChoice] = React.useState(DOWNLOAD_CHOICE);
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const insureds = useQuery({
    queryKey: ['policy-additional-insured', policy.id],
    queryFn: () =>
      fetchAdditonalInsured({ policyId: policy.id }).then(
        (response: { data: AdditionalInsured[] | '' }) =>
          response.data === '' ? [] : response.data
      ),
    onError: handleAPIError('Unable to fetch additional insured info.'),
  });

  const holderForm = useForm<CertificateHolderValues>({
    resolver: yupResolver(holderSchema(isAU)),
  });

  const download = async () => {
    setBusy(true);
    try {
      const { data: url } = await downloadPolicyCertificate(
        policy.accountId,
        policy.id,
        moment(policy.created).unix()
      );
      saveBlobFile(
        await downloadAWSDoc(url),
        `${policy.companyName}-${policy.policyNumber}-Cowbell-Policy-Certificate`,
        '.pdf'
      );
      close();
      notifications.show({
        type: 'success',
        title: 'Policy certificate successfully generated',
        autoHideDuration: 2000,
      });
    } catch (error) {
      handleAPIError(
        'An error occurred finding the document. Please try again'
      )(error as Error);
    } finally {
      setBusy(false);
    }
  };

  const email = async (holder?: CertificateHolderValues) => {
    setBusy(true);
    try {
      await sendPolicyCertificateEmail(
        { productType: policy.product },
        certificateEmailPayload(policy, choice, holder)
      );
      setSent(true);
    } catch (error) {
      handleAPIError('Unable to request sending of policy certificate email.')(
        error as Error
      );
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <UiModal
        opened={open}
        onClose={close}
        title="Generate Policy Certificate"
        size="md"
        centered
        zIndex={MODAL_Z_INDEX}
      >
        <Stack gap="md">
          <Text>
            Thank you for your request. You will get an email shortly, with the
            Certificate of Insurance.
          </Text>
          <Group justify="flex-end">
            <Button onClick={close}>Okay</Button>
          </Group>
        </Stack>
      </UiModal>
    );
  }

  return (
    <UiModal
      opened={open}
      onClose={close}
      title="Generate Policy Certificate"
      size="xl"
      centered
      zIndex={MODAL_Z_INDEX}
    >
      <Stack gap="md">
        <RadioGroup
          value={choice}
          onChange={setChoice}
          name="certificate-action"
        >
          <Stack gap="sm">
            <Radio
              value={DOWNLOAD_CHOICE}
              label="Download Certificate of Insurance"
            />
            {(insureds.data ?? []).map((insured) => (
              <Radio
                key={insuredChoice(insured)}
                value={insuredChoice(insured)}
                label={additionalInsuredLabel(insured)}
                // An insured's name can be one long unbroken word; it wraps rather than running out of the modal.
                styles={{ label: { overflowWrap: 'anywhere' } }}
              />
            ))}
            <Radio value={HOLDER_CHOICE} label="Certificate Holder" />
          </Stack>
        </RadioGroup>

        {choice === HOLDER_CHOICE && (
          <FormProvider {...holderForm}>
            <form
              id={HOLDER_FORM_ID}
              onSubmit={holderForm.handleSubmit(email)}
              noValidate
            >
              <Stack gap="sm">
                <CertificateHolderFields />
                <Text size="sm" c="dimmed" fs="italic" ta="center">
                  Note: By choosing this option, you will not be adding the
                  certificate holder as an additional insured on the policy.
                </Text>
              </Stack>
            </form>
          </FormProvider>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          {choice === DOWNLOAD_CHOICE && (
            <Button loading={busy} onClick={download}>
              Download
            </Button>
          )}
          {insuredIdOf(choice) !== null && (
            <Button loading={busy} onClick={() => email()}>
              Email me
            </Button>
          )}
          {choice === HOLDER_CHOICE && (
            <Button type="submit" form={HOLDER_FORM_ID} loading={busy}>
              Email me
            </Button>
          )}
        </Group>
      </Stack>
    </UiModal>
  );
};

export default DownloadEmailCertificate;

export const DownloadEmailCertificateConfig = {
  DownloadEmailCertificate: {
    component: DownloadEmailCertificate,
    config: {
      title: 'Generate Policy Certificate',
      override: true,
    },
  },
};
